import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import PriceCalculator from '../home/components/PriceCalculator';

export default function TaxiUtrechtEindhovenAirportPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Set page title and meta tags
    document.title = 'Taxi Utrecht Eindhoven Airport | 24/7 Airport Taxi & Vaste Prijs';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Taxi van Utrecht naar Eindhoven Airport nodig? 24/7 beschikbaar, vaste prijs mogelijk. Direct reserveren.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'taxi utrecht eindhoven airport, eindhoven airport taxi utrecht, airport taxi utrecht eindhoven');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://www.utrechtairporttaxi.nl/taxi-utrecht-eindhoven-airport');
    }

    // Add FAQ Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const faqs = [
    {
      question: 'Rijden jullie ook vroeg?',
      answer: 'Ja, 24/7.'
    },
    {
      question: 'Is bagage inbegrepen?',
      answer: 'Ja.'
    },
    {
      question: 'Hoe lang duurt de rit naar Eindhoven Airport?',
      answer: 'Gemiddeld 45-60 minuten, afhankelijk van verkeer. Wij plannen altijd ruim in.'
    },
    {
      question: 'Wat kost een taxi van Utrecht naar Eindhoven Airport?',
      answer: 'Voor veel ritten is een vaste prijs mogelijk. Neem contact op voor een offerte op maat.'
    },
    {
      question: 'Kunnen jullie mij ook ophalen op Eindhoven Airport?',
      answer: 'Ja, geef je vluchtnummer door en wij volgen je vlucht. Bij vertraging passen we de ophaaltijd automatisch aan.'
    },
    {
      question: 'Zijn er extra kosten voor vroege of late ritten?',
      answer: 'Bij een vaste prijs zijn alle kosten inbegrepen, ongeacht het tijdstip.'
    },
    {
      question: 'Hoeveel personen kunnen er mee?',
      answer: 'Standaard 4 personen met bagage. Grotere groepen? Geef dit bij reservering door.'
    },
    {
      question: 'Hoe reserveer ik een taxi naar Eindhoven Airport?',
      answer: 'Bel ons, stuur een WhatsApp of gebruik het online formulier. Je ontvangt direct een bevestiging.'
    }
  ];

  const scrollToBooking = () => {
    const bookingSection = document.getElementById('calculator');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/532651de350fc3df04d00948dc20c9ac.jpg')] bg-cover bg-center"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Taxi Utrecht – Eindhoven Airport
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
              Comfortabele airport taxi van Utrecht naar Eindhoven Airport. Altijd op tijd en vaste prijs mogelijk.
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={scrollToBooking}
                className="px-6 sm:px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line mr-2"></i>Reserveer Nu & Zie Prijs
              </button>
              <a href="tel:+31658824429" className="px-6 sm:px-8 py-4 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap">
                <i className="ri-phone-line mr-2"></i>Bel Direct
              </a>
              <a href="https://wa.me/31658824429" className="px-6 sm:px-8 py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap">
                <i className="ri-whatsapp-line mr-2"></i>WhatsApp Reserveren
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <div id="calculator">
        <PriceCalculator />
      </div>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Taxi Utrecht naar Eindhoven Airport</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Een <strong>taxi van Utrecht naar Eindhoven Airport</strong> biedt rust en zekerheid. Geen overstappen of parkeerproblemen. Wij halen je op en brengen je rechtstreeks naar de luchthaven.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Onze chauffeurs rijden dagelijks deze route en plannen altijd ruim. Voor veel ritten is een <strong>vaste prijs</strong> mogelijk, zodat je vooraf weet waar je aan toe bent.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ook <strong>ophalen vanaf Eindhoven Airport naar Utrecht</strong> is mogelijk.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Waarom Kiezen voor Taxi naar Eindhoven Airport?</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              <strong>Eindhoven Airport</strong> is een populaire luchthaven voor vakantie- en zakenreizigers. Met onze taxi service reis je comfortabel en zonder zorgen. Geen gedoe met parkeren, geen overstappen met het openbaar vervoer.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Onze chauffeurs kennen de route van Utrecht naar Eindhoven Airport perfect en houden rekening met verkeersdrukte op de A27 en A2. Voor vroege vluchten of late aankomsten zijn wij 24/7 beschikbaar.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Reistijd en Route-informatie</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              De <strong>reistijd van Utrecht naar Eindhoven Airport</strong> bedraagt gemiddeld 45 tot 60 minuten. De route loopt meestal via de A27 richting Breda en vervolgens de A2 naar Eindhoven. Bij drukte kiezen onze chauffeurs automatisch de snelste alternatieve route.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Wij adviseren om minimaal 2 uur voor vertrek van je vlucht op Eindhoven Airport te zijn. Voor Europese vluchten is 1,5 uur vaak voldoende. Onze chauffeurs plannen de rit altijd ruim in, zodat je gegarandeerd op tijd aankomt.
            </p>

            <div className="bg-slate-50 rounded-2xl p-8 my-12 border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Voordelen van Onze Taxi Service</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                    <i className="ri-calendar-check-line mr-2 text-amber-500"></i>
                    24/7 Beschikbaar
                  </h4>
                  <p className="text-slate-700">Ook voor vroege ochtendvluchten of late aankomsten staan wij voor je klaar.</p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                    <i className="ri-price-tag-3-line mr-2 text-amber-500"></i>
                    Vaste Prijs Mogelijk
                  </h4>
                  <p className="text-slate-700">Vooraf duidelijkheid over de kosten, geen verrassingen achteraf.</p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                    <i className="ri-steering-2-line mr-2 text-amber-500"></i>
                    Ervaren Chauffeurs
                  </h4>
                  <p className="text-slate-700">Professionele chauffeurs die de route dagelijks rijden.</p>
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                    <i className="ri-luggage-cart-line mr-2 text-amber-500"></i>
                    Ruime Bagageruimte
                  </h4>
                  <p className="text-slate-700">Voldoende ruimte voor koffers, handbagage en sportuitrusting.</p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Ophaalservice vanaf Eindhoven Airport</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ook <strong>ophalen vanaf Eindhoven Airport naar Utrecht</strong> behoort tot onze diensten. Geef bij reservering je vluchtnummer door, dan volgen wij je vlucht automatisch. Bij vertraging hoef je ons niet te bellen – wij passen de ophaaltijd aan.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Onze chauffeur wacht in de aankomsthal met een naambordje. Na het ophalen van je bagage loop je direct naar de auto. Geen wachttijden, geen gedoe. Binnen een uur ben je weer thuis in Utrecht.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Comfort en Service</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Onze taxi's zijn <strong>comfortabel, schoon en goed onderhouden</strong>. Alle voertuigen hebben airconditioning, voldoende beenruimte en een ruime bagageruimte. Je kunt onderweg ontspannen, werken of slapen.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Voor zakelijke reizigers bieden wij facturen en vaste afspraken. Regelmatig naar Eindhoven Airport? Vraag naar onze zakelijke tarieven en voorkeursbehandeling.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Hoe Werkt Reserveren?</h2>
            
            <div className="grid md:grid-cols-3 gap-6 my-8">
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">1</div>
                <h3 className="font-bold text-slate-900 mb-2">Neem Contact Op</h3>
                <p className="text-sm text-slate-600">Via telefoon, WhatsApp of het online formulier</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">2</div>
                <h3 className="font-bold text-slate-900 mb-2">Ontvang Bevestiging</h3>
                <p className="text-sm text-slate-600">Met vaste prijs en alle ritdetails</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">3</div>
                <h3 className="font-bold text-slate-900 mb-2">Reis Comfortabel</h3>
                <p className="text-sm text-slate-600">Chauffeur haalt je op en brengt je veilig naar je bestemming</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Lokale Service vanuit Utrecht</h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ophalen in <strong>Utrecht, Nieuwegein, IJsselstein</strong> en omliggende wijken.
            </p>

            <div className="bg-slate-50 rounded-2xl p-8 my-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Direct Reserveren?</h3>
              <p className="text-lg text-slate-700 mb-6">
                Neem contact op voor een vaste prijs of boek direct via WhatsApp.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={scrollToBooking}
                  className="px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line mr-2"></i>Reserveer Nu
                </button>
                <a href="tel:+31658824429" className="px-8 py-4 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>Bel Direct
                </a>
                <a href="https://wa.me/31658824429" className="px-8 py-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap">
                  <i className="ri-whatsapp-line mr-2"></i>WhatsApp
                </a>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Andere Airport Taxi Routes</h2>
            
            <div className="grid md:grid-cols-2 gap-4 my-8">
              <Link to="/taxi-utrecht-schiphol" className="block p-6 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                  <i className="ri-plane-line mr-2 text-amber-500"></i>
                  Taxi Utrecht – Schiphol
                </h4>
                <p className="text-sm text-slate-600">Betrouwbare service naar Schiphol</p>
              </Link>

              <Link to="/taxi-utrecht-rotterdam-airport" className="block p-6 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors border border-slate-200">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center">
                  <i className="ri-plane-line mr-2 text-amber-500"></i>
                  Taxi Utrecht – Rotterdam The Hague Airport
                </h4>
                <p className="text-sm text-slate-600">Direct naar Rotterdam Airport</p>
              </Link>
            </div>
          </article>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 text-center">Veelgestelde Vragen</h2>
          <p className="text-xl text-slate-600 text-center mb-12">Alles over onze taxi service naar Eindhoven Airport</p>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <summary className="text-lg font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                  <span>{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-2xl text-amber-500"></i>
                </summary>
                <p className="mt-4 text-slate-700 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
